import {
  AfterViewInit,
  Component,
  ElementRef,
  viewChild,
  EventEmitter,
  Input,
  Output,
  DestroyRef,
  OnInit,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { Observable } from 'rxjs';
import { IClass } from '../../models/class.model';
import { ClassService } from '../../services/class.service';
import { ReservationService } from '../../services/reservation.service';
import { IUserWithReservations } from '../../models/userWithReservations.model';
import { LayoutService } from '../../layout/layout.service';
import { AuthService } from '../../services/auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorMessageUtils } from '../../utils/ErrorMessageUtils';
import { ToastService } from '../../ui/overlay/toast.service';
import { ConfirmService } from '../../ui/overlay/confirm.service';
import { ClassType } from '../../enums/class-type';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { BadgeComponent } from '../../ui/badge.component';
import { IconComponent } from '../../ui/icon.component';
import { SpinnerComponent } from '../../ui/spinner.component';
import { ButtonDirective } from '../../ui/button.directive';
import { FieldComponent } from '../../ui/field.component';
import { InputDirective } from '../../ui/input.directive';
import { AsyncPipe, DatePipe, TitleCasePipe } from '@angular/common';
@Component({
  selector: 'app-class-detail',
  templateUrl: './class-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BadgeComponent,
    IconComponent,
    SpinnerComponent,
    ButtonDirective,
    FieldComponent,
    InputDirective,
    ReactiveFormsModule,
    AsyncPipe,
    DatePipe,
    TitleCasePipe,
  ],
})
export class ClassDetailComponent implements OnInit, AfterViewInit {
  private readonly toast = inject(ToastService);
  private readonly confirm = inject(ConfirmService);
  inSubmission: boolean = false;
  isDesktop: boolean = false;

  /** What an admin may change in place: the type and the capacity, never the date. */
  readonly classTypes = Object.values(ClassType);
  readonly editType = new FormControl<ClassType>(ClassType.RegularSpinning, {
    nonNullable: true,
  });
  // The form only knows 1..200; the booked-count floor is enforced by the transaction,
  // where it is read live, and reported back as a Dutch message.
  readonly editMax = new FormControl<number>(40, {
    nonNullable: true,
    validators: [Validators.required, Validators.min(1), Validators.max(200)],
  });
  readonly editForm = new FormGroup({
    type: this.editType,
    maxReservations: this.editMax,
  });

  /** Whole percent, so the progressbar does not announce 63.15789473684211. */
  percentFull(cls: {
    currentReservations: number;
    maxReservations: number;
  }): number {
    return cls.maxReservations
      ? Math.round((cls.currentReservations / cls.maxReservations) * 100)
      : 0;
  }

  /**
   * The bar's colour tracks how hard it is to get a bike, not how full the class is.
   * Filled in brand it read as "good" precisely when the class was unbookable; this is
   * the same green/amber/red the booking list already uses for the free count.
   */
  fillClass(cls: {
    currentReservations: number;
    maxReservations: number;
  }): string {
    const free = cls.maxReservations - cls.currentReservations;
    if (free <= 0) {
      return 'bg-danger';
    }
    return free / cls.maxReservations <= 0.2 ? 'bg-warning' : 'bg-success';
  }

  /** The class to show. Set by the admin screen; the component owns no routing. */
  @Input({ required: true }) spinClass!: IClass;
  @Output() closed = new EventEmitter<void>();

  spinClass$!: Observable<IClass | null>;
  users$!: Observable<IUserWithReservations[]>;

  userId: string = '';

  constructor(
    private authService: AuthService,
    private layoutService: LayoutService,
    private classService: ClassService,
    private reservationService: ReservationService,
  ) {
    this.isDesktop = this.layoutService.isDesktop();
    this.authService.user$.pipe(takeUntilDestroyed()).subscribe((user) => {
      this.userId = user?.id ?? '';
    });
  }

  private readonly dialog =
    viewChild.required<ElementRef<HTMLDialogElement>>('dialog');

  ngAfterViewInit(): void {
    this.dialog().nativeElement.showModal();
  }

  ngOnInit(): void {
    this.editForm.patchValue({
      type: this.spinClass.type ?? ClassType.RegularSpinning,
      maxReservations: this.spinClass.maxReservations ?? 40,
    });
    this.spinClass$ = this.classService.getClassById(this.spinClass.id);
    this.users$ =
      this.reservationService.getUsersForClassWithAmountOfReservations(
        this.spinClass.id,
      );
  }

  close(): void {
    this.closed.emit();
  }

  async removeClass() {
    const confirmed = await this.confirm.ask({
      message: 'Ben je zeker dat je deze spinning les wil verwijderen?',
    });
    if (!confirmed) {
      return;
    }
    try {
      await this.classService.removeClass(this.spinClass.id);
      this.closed.emit();
    } catch (error) {
      this.toast.error(
        ErrorMessageUtils.determineFrontendMessageBasedOnError(error),
      );
    }
  }

  async saveEdit() {
    if (this.editForm.invalid || this.inSubmission) {
      return;
    }
    this.inSubmission = true;
    try {
      await this.classService.updateClass(this.spinClass.id, {
        type: this.editType.value,
        maxReservations: Number(this.editMax.value),
      });
      this.toast.success('Les aangepast.');
    } catch (error) {
      this.toast.error(
        ErrorMessageUtils.determineFrontendMessageBasedOnError(error),
      );
    } finally {
      this.inSubmission = false;
    }
  }

  async addReservation(userId: string) {
    this.inSubmission = true;
    await this.reservationService
      .addReservation(userId, this.spinClass.id)
      .then(() => {
        this.toast.success('1 fiets toegevoegd!');
      })
      .catch((error) => {
        this.toast.error(
          ErrorMessageUtils.determineFrontendMessageBasedOnError(error),
        );
      })
      .finally(() => {
        this.inSubmission = false;
      });
  }

  async addAdminReservation() {
    return this.addReservation(this.userId);
  }

  async removeReservation(userId: string) {
    this.inSubmission = true;
    await this.reservationService
      .removeReservation(userId, this.spinClass.id)
      .then(() => {
        this.toast.success('1 fiets verwijderd!');
      })
      .catch((error) => {
        this.toast.error(
          ErrorMessageUtils.determineFrontendMessageBasedOnError(error),
        );
      })
      .finally(() => {
        this.inSubmission = false;
      });
  }
}
